// Trial API fns — carved verbatim from `src/api/scribeAPIV2.ts` (which now
// re-exports from here) over the E5 configured-client registry.
//
// DOMAIN RULING (D1, ARCFE-972): `extendReverseTrial`, `revertReverseTrial`
// and `getGamifyTrialExtension` were `@domain REVIEW`;
// `activateClinicianTrial` was `@domain workspace` on a `/billings/*` route.
// All four serve exactly one commerce-claimed hook each and sit inside the
// 2.13-ratified trial boundary, so they are ruled `@domain commerce` at
// carve (the dictation P0.4-class precedent).
//
// NOT carved here, deliberately: `activatePracticeTrial`,
// `activatePromotedFreeTrial` and `deactivatePromotedFreeTrial` sit on
// neighbouring lines in the hub and look like this cohort, but no
// commerce-claimed hook in the measured D-series calls them. Sweeping them
// in would claim surface the census never measured — they stay for a later
// slice with a real consumer.

import { getScribeHttpClients } from '@heidi/api/client';
import {
  type ActivateClinicianTrialResponse,
  type ExtendReverseTrialRequest,
  type GamifyTrialExtensionResponse,
} from '../../contracts';
import { type CommerceHttpClient } from '../config';

const http = (): CommerceHttpClient => getScribeHttpClients().scribeHttpV2;

/**
 * Activate clinician evidence trial based on user's current subscription tier.
 * Returns success status and trial info after activation.
 *
 * Backend makes synchronous Stripe API calls and returns the result directly.
 * @domain commerce
 */
export const activateClinicianTrial = () => {
  return http().post<ActivateClinicianTrialResponse>(
    '/billings/activate-clinician-trial'
  );
};

/**
 * Heidi 2 signup trial: 14-day Clinician Standard Stripe trial + Standard
 * allowance for a FREE Heidi 2 workspace (ml-scribe MONEY-987, PR 18915).
 * Backend 404s unless `release-heidi-2-opt-in` is on; workspace admins only.
 * @domain commerce
 */
export const activateHeidi2ClinicianTrial = () =>
  http().post<ActivateClinicianTrialResponse>(
    '/billings/activate-heidi-2-clinician-trial'
  );

/** @domain commerce */
export const extendReverseTrial = (hours: number) => {
  const request: ExtendReverseTrialRequest = {
    hours: hours,
  };
  return http().post('/user/extend-reverse-trial', request);
};

/** @domain commerce */
export const revertReverseTrial = () => {
  return http().post('/user/revert-reverse-trial');
};

/** @domain commerce */
export const getGamifyTrialExtension = () => {
  return http().get<GamifyTrialExtensionResponse>('/user/gamify-trial-extension');
};

/**
 * @domain commerce
 * Hand-written, like its record_gamify_template_selected / record_gamify_source_control_used
 * siblings (`src/api/scribeAPIV2.ts`) — this endpoint's client fn is not
 * generated by `pnpm run schema:dev`, only its path/operation types are.
 * Path = the confirmed suffix `record-desktop-app-download-clicked`, prefixed
 * with `/user/gamify-trial-extension/` to match those two siblings' base path
 * exactly — double-check against the merged backend PR before shipping.
 */
export const recordGamifyDesktopAppDownloadClicked = () => {
  return http().post<void>(
    '/user/gamify-trial-extension/record-desktop-app-download-clicked'
  );
};
